import { useEffect, useRef } from 'react'
import { Tag } from 'antd'
import { ingestCaptureText, isScannerArmed } from '@/services/scannerService'
import { isScannerSessionActive, useRfidSessionStore } from '@/stores/rfidSessionStore'
import { cn } from '@/utils/cn'

type Props = {
  /** When true, keep the capture input focused so wedge keys land here. */
  armed?: boolean
  className?: string
  showStatus?: boolean
  /** Visible large target for trigger verification (connect screen). */
  visible?: boolean
  placeholder?: string
}

/**
 * Capture input marked `data-scan-capture="true"` so HID wedge keystrokes are
 * captured (global listener + input backup path).
 */
export function ScanCaptureHint({
  armed,
  className,
  showStatus = true,
  visible = false,
  placeholder = 'Click here, then pull the RFD trigger',
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const scannerConnected = useRfidSessionStore((s) => s.scannerConnected)
  const scannerPending = useRfidSessionStore((s) => s.scannerPending)
  const deviceName = useRfidSessionStore((s) => s.deviceConnected?.rfid_device)

  const sessionActive = isScannerSessionActive({ scannerConnected, scannerPending })
  const shouldArm = armed ?? (sessionActive && isScannerArmed())

  useEffect(() => {
    if (!shouldArm) return
    const el = inputRef.current
    if (!el) return

    const focus = () => {
      if (document.activeElement === el) return
      const active = document.activeElement
      if (!(active instanceof HTMLElement)) {
        el.focus({ preventScroll: true })
        return
      }
      if (active.dataset.noWedge === 'true') return
      if (active.dataset.scanCapture === 'true') return
      if (active.isContentEditable) return

      const tag = active.tagName
      if (tag === 'TEXTAREA' || tag === 'SELECT') return
      if (tag === 'INPUT') {
        const type = ((active as HTMLInputElement).type || 'text').toLowerCase()
        const textual = new Set([
          'text',
          'search',
          'email',
          'tel',
          'url',
          'password',
          'number',
          'date',
          'datetime-local',
          'time',
          'month',
          'week',
          '',
        ])
        if (textual.has(type)) return
      }

      el.focus({ preventScroll: true })
    }

    focus()
    const id = window.setInterval(focus, 500)
    return () => window.clearInterval(id)
  }, [shouldArm])

  const onInput = (value: string) => {
    if (!shouldArm) return
    ingestCaptureText(value)
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div className={cn(visible ? 'w-full' : 'flex items-center gap-2', className)}>
      <input
        ref={inputRef}
        type="text"
        data-scan-capture="true"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        tabIndex={shouldArm ? 0 : -1}
        placeholder={visible ? placeholder : undefined}
        aria-label={visible ? placeholder : undefined}
        aria-hidden={!visible}
        className={
          visible
            ? 'rfid-field__control w-full border-[1.5px] border-amber-400 bg-amber-50 font-mono text-[13px] font-bold'
            : 'pointer-events-none absolute h-px w-px opacity-0'
        }
        style={visible ? undefined : { left: -9999, top: 0 }}
        onChange={(e) => onInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault()
            ingestCaptureText(inputRef.current?.value ?? '', { submit: true })
            if (inputRef.current) inputRef.current.value = ''
          }
        }}
      />
      {showStatus && !visible ? (
        <Tag color={scannerConnected ? 'success' : scannerPending ? 'warning' : 'default'}>
          {scannerConnected
            ? `Connected${deviceName ? ` · ${deviceName}` : ''}`
            : scannerPending
              ? `Waiting for tag${deviceName ? ` · ${deviceName}` : ''}`
              : 'Disconnected'}
        </Tag>
      ) : null}
    </div>
  )
}
